import React, { useMemo } from "react";
import _ from "lodash";
import { AddStaffIllustration } from "@toddle-edu/ds-theme";

import { graphql, compose } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import DualPanelManageMembers from "AppComponents/DualPanelManageMembersV2";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import {
  getCourseUsersQueryV3,
  getGradeUsersQueryV3,
  getOrganizationCourseUsersQueryV3,
  getMessageV2UsersWithStudentParentQueryV3,
} from "MessagingV2/modules/MessagingQuery";
import { getOrganizationId, getUserId } from "modules/CommonSelectors";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import {
  COHORT_TYPE,
  DUAL_PANEL_VIEW_MODE,
} from "AppComponents/DualPanelManageMembersV2/module/utils";
import {
  CHAT_STATUS_ENUM,
  isMessagingRnpEnabled,
  isCreateChatsAndChannelsEnableForAll,
  RNP_CAN_CREATE_AND_MANAGE_CHATS_AND_CHANNELS_FOR_CLASS,
} from "MessagingV2/modules/Utils";
import { prepareLoadMoreFeed } from "Utils";
import { FILTER_KEYS } from "../Channels/CreateChannelModal/Filter";
import CohortSubText from "./components/CohortSubText";

const getIncludeFlags = selectedUserTypes => ({
  includeStudents:
    _.isEmpty(selectedUserTypes) || _.includes(selectedUserTypes, STUDENT),
  includeStaff:
    _.isEmpty(selectedUserTypes) || _.includes(selectedUserTypes, STAFF),
  includeParents:
    _.isEmpty(selectedUserTypes) || _.includes(selectedUserTypes, PARENT),
});

const getMessagingCoursesCohorts = (edges, selectedUserTypes) => {
  const { includeStudents, includeStaff, includeParents } = getIncludeFlags(
    selectedUserTypes
  );
  const coursesCohorts = [];
  _.forEach(edges, course => {
    const {
      students = {},
      collaborator = {},
      familyMembers = {},
      id,
      title,
      grades = [],
      curriculumProgram,
    } = _.get(course, "node", {});
    if (includeStudents) {
      const studentsList = _.map(_.get(students, "edges", []), "node");
      coursesCohorts.push({
        id,
        title,
        type: STUDENT,
        userList: studentsList,
        grades,
        curriculumProgram,
      });
    }
    if (includeStaff) {
      const collaboratorList = _.map(_.get(collaborator, "edges", []), "node");
      coursesCohorts.push({
        id,
        title,
        type: STAFF,
        userList: collaboratorList,
        grades,
        curriculumProgram,
      });
    }
    if (includeParents) {
      const familyMembersList = _.map(
        _.get(familyMembers, "edges", []),
        "node"
      );
      coursesCohorts.push({
        id,
        title,
        type: PARENT,
        userList: familyMembersList,
        grades,
        curriculumProgram,
      });
    }
  });
  return coursesCohorts;
};

const getMessagingGradesCohorts = (edges, selectedUserTypes) => {
  const { includeStudents, includeStaff, includeParents } = getIncludeFlags(
    selectedUserTypes
  );
  const gradeCohorts = [];
  _.forEach(edges, gradeItem => {
    const grade = _.get(gradeItem, "node", gradeItem);
    const {
      allStudents = [],
      allStaffs = [],
      allParents = [],
      id,
      name: title,
      curriculumProgram,
    } = grade;
    if (includeStudents) {
      gradeCohorts.push({
        id,
        title,
        type: STUDENT,
        userList: allStudents,
        curriculumProgram,
      });
    }
    if (includeStaff) {
      gradeCohorts.push({
        id,
        title,
        type: STAFF,
        userList: allStaffs,
        curriculumProgram,
      });
    }
    if (includeParents) {
      gradeCohorts.push({
        id,
        title,
        type: PARENT,
        userList: allParents,
        curriculumProgram,
      });
    }
  });
  return gradeCohorts;
};

const getSelectedUserTypes = props =>
  _.get(
    props,
    ["customQueryWrapperFields", "selectedFilters", FILTER_KEYS.USER_TYPE],
    []
  );

const getSchoolFilter = props => {
  const ids = _.get(
    props,
    ["customQueryWrapperFields", "selectedFilters", FILTER_KEYS.SCHOOL],
    []
  );
  return _.isEmpty(ids) ? {} : { curriculumProgramIds: ids };
};

const buildComposedWrapperV3 = () =>
  compose(
    // ── Members ──────────────────────────────────────────────────────────────
    graphql(getMessageV2UsersWithStudentParentQueryV3, {
      name: "getMessageV2UsersWithStudentParent",
      skip: props => {
        const typeFilter = props.customQueryWrapperFields?.typeFilter;
        return !!typeFilter && typeFilter !== DUAL_PANEL_VIEW_MODE.MEMBERS;
      },
      options: props => ({
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          portalType: "PLANNER",
          includeCurriculumPrograms: true,
          input: {
            first: 20,
            userType: _.isEmpty(getSelectedUserTypes(props))
              ? [STAFF, STUDENT, PARENT]
              : getSelectedUserTypes(props),
            searchText: props.searchText,
            userStatus: "ACTIVE",
            chatStatus: CHAT_STATUS_ENUM.ALL,
            includePaidUsersOnly: false,
            includeInitiatedChatUsers: false,
            ...getSchoolFilter(props),
          },
        },
      }),
      props: ({ getMessageV2UsersWithStudentParent, ownProps }) => {
        const { isLoading, isData } = ownProps;
        const queryVariables = _.get(
          getMessageV2UsersWithStudentParent,
          "variables",
          {}
        );
        const cachedData = _.get(
          getMessageV2UsersWithStudentParent,
          "node",
          {}
        );
        const edges = _.get(cachedData, "messageV2Users.edges", []);
        const hasNextPage = _.get(
          cachedData,
          "messageV2Users.pageInfo.hasNextPage",
          false
        );
        return {
          isData: !_.isEmpty(cachedData) && isData,
          isLoading:
            _.includes(
              [1, 2],
              getMessageV2UsersWithStudentParent["networkStatus"]
            ) || isLoading,
          cohortParamsMap: {
            [COHORT_TYPE.MEMBERS]: {
              id: "members_all",
              index: 0,
              type: COHORT_TYPE.MEMBERS,
              cohorts: edges,
              hasNextPage,
              networkStatus:
                getMessageV2UsersWithStudentParent["networkStatus"],
              isFetchingMore: _.includes(
                [3],
                getMessageV2UsersWithStudentParent["networkStatus"]
              ),
              loadMoreFeed: prepareLoadMoreFeed({
                query: getMessageV2UsersWithStudentParentQueryV3,
                variables: {
                  ...queryVariables,
                  input: {
                    ..._.get(queryVariables, "input", {}),
                    before: _.get(
                      cachedData,
                      "messageV2Users.pageInfo.endCursor"
                    ),
                  },
                },
                queryData: getMessageV2UsersWithStudentParent,
                key: "messageV2Users",
              }),
            },
          },
        };
      },
    }),

    // ── Classes (admin path) ──────────────────────────────────────────────────
    graphql(getOrganizationCourseUsersQueryV3, {
      name: "getOrgCourseUsers",
      skip: props => {
        if (isMessagingRnpEnabled() || !getIsAdminByRole()) {
          return true;
        }
        const typeFilter = props.customQueryWrapperFields?.typeFilter;
        return !!typeFilter && typeFilter !== DUAL_PANEL_VIEW_MODE.CLASSES;
      },
      options: props => ({
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          first: 20,
          portalType: "PLANNER",
          includeCurriculumPrograms: true,
          filters: {
            searchText: props.searchText,
            ...getSchoolFilter(props),
          },
          ...getIncludeFlags(getSelectedUserTypes(props)),
        },
      }),
      props: ({ getOrgCourseUsers, ownProps }) => {
        const { isLoading, isData } = ownProps;
        const queryVariables = _.get(getOrgCourseUsers, "variables", {});
        const cachedData = _.get(getOrgCourseUsers, "node", {});
        const edges = _.get(cachedData, "courses.edges", []);
        const hasNextPage = _.get(
          cachedData,
          "courses.pageInfo.hasNextPage",
          false
        );
        return {
          isData: !_.isEmpty(cachedData) && isData,
          isLoading:
            _.includes([1, 2], getOrgCourseUsers["networkStatus"]) || isLoading,
          cohortParamsMap: {
            [COHORT_TYPE.CLASSES]: {
              id: "classes_admin",
              index: 0,
              type: COHORT_TYPE.CLASSES,
              cohorts: getMessagingCoursesCohorts(
                edges,
                getSelectedUserTypes(ownProps)
              ),
              hasNextPage,
              networkStatus: getOrgCourseUsers["networkStatus"],
              isFetchingMore: _.includes(
                [3],
                getOrgCourseUsers["networkStatus"]
              ),
              loadMoreFeed: prepareLoadMoreFeed({
                query: getOrganizationCourseUsersQueryV3,
                variables: {
                  ...queryVariables,
                  after: _.get(cachedData, "courses.pageInfo.endCursor"),
                },
                queryData: getOrgCourseUsers,
                key: "courses",
              }),
            },
          },
        };
      },
    }),

    // ── Classes (RnP path) ────────────────────────────────────────────────────
    graphql(getCourseUsersQueryV3, {
      name: "getCourseUsers",
      skip: props => {
        if (!isMessagingRnpEnabled() && getIsAdminByRole()) {
          return true;
        }
        const typeFilter = props.customQueryWrapperFields?.typeFilter;
        return !!typeFilter && typeFilter !== DUAL_PANEL_VIEW_MODE.CLASSES;
      },
      options: props => ({
        fetchPolicy: "cache-and-network",
        variables: {
          id: getUserId(),
          organizationId: getOrganizationId(),
          first: 20,
          portalType: "PLANNER",
          includeCurriculumPrograms: true,
          filters: {
            searchText: props.searchText,
            ...getSchoolFilter(props),
            ...(isMessagingRnpEnabled()
              ? {
                  fetchOnlyMappedCourses: false,
                  controlParameters: [
                    RNP_CAN_CREATE_AND_MANAGE_CHATS_AND_CHANNELS_FOR_CLASS,
                  ],
                }
              : {}),
          },
          ...getIncludeFlags(getSelectedUserTypes(props)),
        },
      }),
      props: ({ getCourseUsers, ownProps }) => {
        const { isLoading, isData } = ownProps;
        const queryVariables = _.get(getCourseUsers, "variables", {});
        const cachedData = _.get(getCourseUsers, "node", {});
        const edges = _.get(cachedData, "coursesV2.edges", []);
        const hasNextPage = _.get(
          cachedData,
          "coursesV2.pageInfo.hasNextPage",
          false
        );
        return {
          isData: !_.isEmpty(cachedData) && isData,
          isLoading:
            _.includes([1, 2], getCourseUsers["networkStatus"]) || isLoading,
          cohortParamsMap: {
            [COHORT_TYPE.CLASSES]: {
              id: "classes_rnp",
              index: 0,
              type: COHORT_TYPE.CLASSES,
              cohorts: getMessagingCoursesCohorts(
                edges,
                getSelectedUserTypes(ownProps)
              ),
              hasNextPage,
              networkStatus: getCourseUsers["networkStatus"],
              isFetchingMore: _.includes([3], getCourseUsers["networkStatus"]),
              loadMoreFeed: prepareLoadMoreFeed({
                query: getCourseUsersQueryV3,
                variables: {
                  ...queryVariables,
                  after: _.get(cachedData, "coursesV2.pageInfo.endCursor"),
                },
                queryData: getCourseUsers,
                key: "coursesV2",
              }),
            },
          },
        };
      },
    }),

    // ── Grades ────────────────────────────────────────────────────────────────
    graphql(getGradeUsersQueryV3, {
      name: "getGradeUsers",
      skip: props => {
        if (
          isMessagingRnpEnabled() &&
          !isCreateChatsAndChannelsEnableForAll()
        ) {
          return true;
        }
        const typeFilter = props.customQueryWrapperFields?.typeFilter;
        return !!typeFilter && typeFilter !== DUAL_PANEL_VIEW_MODE.GRADES;
      },
      options: props => ({
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          first: 20,
          portalType: "PLANNER",
          includeCurriculumPrograms: true,
          filters: {
            searchText: props.searchText,
            ...getSchoolFilter(props),
          },
          ...getIncludeFlags(getSelectedUserTypes(props)),
        },
      }),
      props: ({ getGradeUsers, ownProps }) => {
        const { isLoading, isData } = ownProps;
        const queryVariables = _.get(getGradeUsers, "variables", {});
        const cachedData = _.get(getGradeUsers, "node", {});
        const edges = _.get(cachedData, "gradesV2.edges", []);
        const hasNextPage = _.get(
          cachedData,
          "gradesV2.pageInfo.hasNextPage",
          false
        );
        return {
          isData: !_.isEmpty(cachedData) && isData,
          isLoading:
            _.includes([1, 2], getGradeUsers["networkStatus"]) || isLoading,
          cohortParamsMap: {
            [COHORT_TYPE.GRADES]: {
              id: "grades_all",
              index: 0,
              type: COHORT_TYPE.GRADES,
              cohorts: getMessagingGradesCohorts(
                edges,
                getSelectedUserTypes(ownProps)
              ),
              hasNextPage,
              networkStatus: getGradeUsers["networkStatus"],
              isFetchingMore: _.includes([3], getGradeUsers["networkStatus"]),
              loadMoreFeed: prepareLoadMoreFeed({
                query: getGradeUsersQueryV3,
                variables: {
                  ...queryVariables,
                  after: _.get(cachedData, "gradesV2.pageInfo.endCursor"),
                },
                queryData: getGradeUsers,
                key: "gradesV2",
              }),
            },
          },
        };
      },
    }),

    withLoader
  )(DualPanelManageMembers.CohortList);

const DualPanelQueryWrapperV3 = props => {
  const ComposedWrapper = useMemo(() => buildComposedWrapperV3(), []);
  return <ComposedWrapper {...props} />;
};

const renderCohortSubText = ({ cohort, type, t }) => (
  <CohortSubText cohort={cohort} type={type} t={t} />
);

const SEARCH_PLACEHOLDER_MAP = {
  [DUAL_PANEL_VIEW_MODE.MEMBERS]: "announcement:search_members",
  [DUAL_PANEL_VIEW_MODE.CLASSES]: "announcement:search_classes",
  [DUAL_PANEL_VIEW_MODE.GRADES]: "announcement:search_grades",
};

export const metadataPropsV3 = {
  emptyStateSubtitle: "common:add_members_v3_search_empty_state_subtitle",
  countUsersSelectedText: "common:count_member_selected",
  selectedUsersListText: "common:selected_members_list",
  disabledUserTooltipText: "common:user_already_exists_in_channel",
  disabledNestedFamilyTooltipText:
    "common:family_member_already_exist_in_channel",
  viewMembersEmptyStateTitle: "common:no_members_have_been_selected_yet",
  viewMembersEmptyStateSubtitle: "common:select_members_to_view_here",
  viewMembersEmptyStateIllustration: AddStaffIllustration,
  showListHeader: false,
  getSearchPlaceholder: ({ t, customQueryWrapperFields }) => {
    const typeFilter = _.get(customQueryWrapperFields, "typeFilter");
    const customPlaceholder = _.get(SEARCH_PLACEHOLDER_MAP, [typeFilter]);
    return customPlaceholder
      ? t(customPlaceholder)
      : t("common:search_placeholder");
  },
  renderCohortSubText,
};

export default DualPanelQueryWrapperV3;
